import { graphql } from 'gatsby';
import { useSwipeable } from 'react-swipeable';
import ResponsiveImage from '@/components/ResponsiveImage/ResponsiveImage';
import useTabs from '@/hooks/useTabs';
import N5050BlockTextItem from '@/components/N5050Block/components/N5050BlockTextItem/N5050BlockTextItem';
import ControlsCarousel from '@/components/ControlsCarousel/ControlsCarousel';
import { applyPaddingCSS } from '@/utils/generatePadding';
import {
  StyledContainer,
  StyledListItem,
  StyledTabTitle,
  StyledList,
  StyledContent,
  StyledSection,
  StyledTextWrapper,
  StyledImageWrapper,
  StyledTabDescription,
  StyledControlContainer,
} from './ComboBlock.styles';
import { type BlockHTMLId } from '@/utils/renderBlock';

const ComboBlock = ({
  comboBlockElements: elements,
  system,
  id,
}: Queries.COMBO_BLOCKFragment & BlockHTMLId) => {
  const blocks = elements.combo_block_items.value;
  const {
    activeIndex,
    setActiveIndex,
    handlePrevClick,
    handleNextClick,
    isPrevArrowDisabled,
    isNextArrowDisabled,
  } = useTabs({
    numberOfItems: blocks.length,
  });
  const swipeHandlers = useSwipeable({
    onSwipedLeft: handleNextClick,
    onSwipedRight: handlePrevClick,
  });

  const activeTab = blocks[activeIndex];
  const isBlockTextItem =
    activeTab?.elements.content.value.__typename === 'kontent_item_n5050_block_text_item';
  const isBlockImage =
    activeTab?.elements.content.value.__typename === 'kontent_item_responsive_image';

  return (
    <StyledSection
      data-codename={system.codename}
      {...swipeHandlers}
      css={applyPaddingCSS(elements)}
      isReverseControlsPosition={isBlockImage}
      id={id}
    >
      <StyledControlContainer>
        <ControlsCarousel
          activeIndex={activeIndex}
          isNextArrowDisabled={isNextArrowDisabled}
          isPrevArrowDisabled={isPrevArrowDisabled}
          numberOfItems={blocks.length}
          onClickNext={handleNextClick}
          onClickPrevious={handlePrevClick}
          setActiveIndex={setActiveIndex}
        />
      </StyledControlContainer>
      <StyledContainer>
        <StyledList isHiddenInMobile={isBlockTextItem}>
          {blocks.map((block, index) => {
            const isActive = activeIndex === index;
            const title = block.elements.tab_title.value;
            const description = block.elements.tab_description?.value;
            const hasDescription =
              block?.elements.content.value.__typename === 'kontent_item_responsive_image' &&
              description &&
              isActive;

            return (
              <StyledListItem
                className="group"
                isActive={isActive}
                key={index}
                onClick={() => {
                  setActiveIndex(index);
                }}
                isHiddenWhenNotActiveInMobile={isBlockImage}
              >
                <StyledTabTitle>{title}</StyledTabTitle>
                {hasDescription && <StyledTabDescription>{description}</StyledTabDescription>}
              </StyledListItem>
            );
          })}
        </StyledList>
        {isBlockTextItem && (
          <StyledContent>
            <StyledTextWrapper>
              <N5050BlockTextItem {...activeTab.elements.content.value} />
            </StyledTextWrapper>
          </StyledContent>
        )}
        {isBlockImage && (
          <StyledContent>
            <StyledImageWrapper>
              <ResponsiveImage {...activeTab.elements.content.value} />
            </StyledImageWrapper>
          </StyledContent>
        )}
      </StyledContainer>
    </StyledSection>
  );
};

export const fragment = graphql`
  fragment COMBO_BLOCK on kontent_item_combo_block {
    system {
      codename
    }
    comboBlockElements: elements {
      ...COMPONENT_A_B_TEST
      ...COMPONENT_PERSONALIZATION
      ...PADDING
      combo_block_items {
        value {
          ... on kontent_item_combo_block_item {
            elements {
              tab_title {
                value
              }
              tab_description {
                value
              }
              content {
                value {
                  __typename
                  ...N5050_BLOCK_TEXT_ITEM
                  ...RESPONSIVE_IMAGE
                }
              }
            }
          }
        }
      }
    }
  }
`;

export default ComboBlock;
